import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

export function BackupAlert() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/backups/nightly-email-status").then((r) => setS(r.data)).catch(() => setS(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!s?.alert) return null;
  const TEXT = {
    restore_check_failed: ["The monthly practice restore check failed", `Checked at ${fmtDateTime(s.at)}: ${s.error}. Your latest backup may not restore correctly.`],
    backup_failed: ["Last night's backup did not run", `The nightly backup failed at ${fmtDateTime(s.at)}: ${s.error}. Create a backup now from Settings.`],
    storage_failed: ["Last night's backup was not saved to cloud storage", `Backup of ${fmtDateTime(s.backup_created_at)} is only in the website database: ${s.error}`],
    email_failed: ["Last night's backup email was not delivered", `Backup of ${fmtDateTime(s.backup_created_at)} was saved, but the email to ${(s.recipients || []).join(", ")} failed: ${s.error}`],
  }[s.kind];
  const retry = async () => {
    setBusy(true);
    try {
      if (s.kind === "restore_check_failed") { const r = await api.post("/backups/restore-check"); r.data.status === "passed" ? toast.success("Restore check passed") : toast.error(r.data.details); }
      else if (s.kind === "storage_failed") { await api.post(`/backups/${s.backup_id}/cloud-copy`); toast.success("Saved to cloud storage"); }
      else { await api.post(`/backups/${s.backup_id}/email`); toast.success("Backup email sent"); }
      load();
    }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3 fade-up" data-testid="backup-email-alert">
      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-amber-900" data-testid="backup-email-alert-title">{TEXT[0]}</div>
        <div className="text-amber-800 mt-0.5" data-testid="backup-email-alert-detail">{TEXT[1]}</div>
      </div>
      <div className="flex gap-2 shrink-0">
        {s.kind !== "backup_failed" && <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={retry} data-testid="backup-email-alert-retry-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} {s.kind === "storage_failed" ? "Save again" : s.kind === "restore_check_failed" ? "Check again" : "Send again"}
        </Button>}
        <Button size="sm" variant="outline" asChild data-testid="backup-email-alert-settings-link"><Link to="/settings">Settings</Link></Button>
      </div>
    </div>
  );
}
